import React, {useEffect, useCallback, useState} from 'react';
import {
  Text,
  View,
  ViewStyle,
  TextStyle,
  ImageStyle,
  StyleSheet,
} from 'react-native';
import {Formik, FormikProps} from 'formik';
import {MultiSelect, Button, TextInput, DatePicker} from 'ui-kits';
import colors from 'theme/Colors';
import {spacing} from 'theme/Units';
import {AddEmployeeValidation, UpdateEmployeeValidation} from 'utils/schema';

import { useSelector, useDispatch } from 'react-redux';
import { allProjectList, projectNextPage } from 'redux/selectors/projectSelector';
import {getOrgID} from 'redux/selectors/authSelector';
import {Project, getProjectsRequest, getProjectsRequestParams } from 'redux/slices/projectSlice';

type propTypes = Readonly<{
  initialValues: {
    org_id?: number;
    emp_id?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    personal_email?: string;
    phone?: string;
    dob?: Date;
    date_of_joining?: Date;
    last_day_of_work?: Date;
    is_active?: boolean;
    project?: number[];
  };
  isEditing?: boolean;
  onSubmit: (values: any) => void;
  onCancel: () => void;
  projects?: Project[];
}>;

export function EmployeeForm(props: propTypes) {
  const {
    initialValues,
    isEditing = false,
    onSubmit,
    onCancel,
  } = props;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={
        isEditing ? UpdateEmployeeValidation : AddEmployeeValidation
      }
      onSubmit={onSubmit}>
      {formikProp => (
        <FormComponent
          {...formikProp}
          initialValues={initialValues}
          isEditing={isEditing}
          onCancel={onCancel}
        />
      )}
    </Formik>
  );
}

type formProps = Readonly<{
  initialValues: propTypes['initialValues'];
  isEditing: boolean;
  onCancel: () => void;
}> &
  FormikProps<propTypes['initialValues']>;
function FormComponent({
  handleChange,
  handleBlur,
  handleSubmit,
  values,
  errors,
  setFieldValue,
  dirty,
  isValid,
  setValues,
  initialValues,
  isEditing,
  onCancel,
}: formProps) {
  const org_id = useSelector(getOrgID);
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const allProjects = useSelector(allProjectList);
  const nextProjectPage = useSelector(projectNextPage);

  useEffect(() => {
    if (initialValues && isEditing) {
      // de-serialize the values
      setValues(initialValues);
    }
  }, [initialValues, setValues, isEditing]);

  const fetchProjects = useCallback(() => {
    const payload: getProjectsRequestParams = {
      org_id: org_id as any,
      page: currentPage,
    };

    dispatch(getProjectsRequest(payload));
  }, [dispatch, org_id, currentPage]);

  useEffect(() => {
    // if employee list is empty then fetch them
    fetchProjects();
  }, [fetchProjects]);

  function fetchNextPage(){
    if (nextProjectPage){
      setCurrentPage(nextProjectPage);
    }
  }

  return (
    <View style={styles.form}>
      <View style={styles.formSecondSection}>
        {isEditing ? (
          <TextInput
            value={values.emp_id}
            error={errors.emp_id}
            placeholder="Employee ID"
            label="Employee ID"
            onChangeText={handleChange('emp_id')}
            onBlur={handleBlur('emp_id')}
            required
          />
        ) : null}
        {isEditing ? (
          <DatePicker
            value={values.last_day_of_work}
            error={errors.last_day_of_work}
            label="Last Working Day"
            onChangeDate={date => setFieldValue('last_day_of_work', date)}
          />
        ) : null}
        <TextInput
          value={values.first_name}
          error={errors.first_name}
          label="First Name"
          placeholder="Enter First Name"
          required
          onChangeText={handleChange('first_name')}
          onBlur={handleBlur('first_name')}
          maxLength={15}
        />
        <TextInput
          value={values.last_name}
          error={errors.last_name}
          label="Last Name"
          placeholder="Enter Last Name"
          onChangeText={handleChange('last_name')}
          maxLength={15}
        />

        {/* only for adding employee */}
        {!isEditing ? (
          <TextInput
            value={values.email}
            error={errors.email}
            label="Official Email"
            placeholder="Enter Official Email ID"
            required
            onChangeText={handleChange('email')}
            onBlur={handleBlur('email')}
          />
        ) : null}

        {!isEditing ? (
          <TextInput
            value={values.personal_email}
            error={errors.personal_email}
            label="Personal Email"
            placeholder="Enter Personal Email ID"
            onChangeText={handleChange('personal_email')}
          />
        ) : null}

        {!isEditing ? (
          <TextInput
            value={values.phone}
            label="Phone Number"
            error={errors.phone}
            placeholder="Enter Phone Number"
            type="numeric"
            maxLength={10}
            onChangeText={handleChange('phone')}
          />
        ) : null}
        {/* for both add and update */}
        <DatePicker
          value={values.dob}
          error={errors.dob}
          label="Date of Birth"
          required
          calenderStyle={styles.dateOfBirthCalender}
          onChangeDate={date => setFieldValue('dob', date)}
        />
        <DatePicker
          value={values.date_of_joining}
          error={errors.date_of_joining}
          label="Date of Joining"
          required
          calenderStyle={styles.dateOfJoinCalender}
          onChangeDate={date => setFieldValue('date_of_joining', date)}
        />
        {/* only for add employee */}
        {!isEditing ? (
          <MultiSelect
            selectedEntries={values.project}
            label="Project"
            keyExtractor={(item: Project) => item.id.toString()}
            options={allProjects}
            searchKeyExtractor={(item: Project) => item.name + item.id}
            renderItem={(item: Project) => <Text>{item.name}</Text>}
            onSave={selected => setFieldValue('project', selected)}
            searchPlaceholder="Search..."
            placeholder="Select one or more project"
            searchHeader="Select Projects"
            dropDownStyle={styles.dropDownStyle}
            fetchNextHandler={fetchNextPage}
          />
        ) : null}
      </View>

      {/* Action Buttons */}
      <View style={styles.actionButtonWrapper}>
        <Button
          text="Cancel"
          variant="secondary"
          onPress={onCancel}
          style={styles.actionButton}
        />
        <Button
          text={isEditing ? 'Update' : 'Save'}
          variant="primary"
          disabled={!dirty || !isValid}
          onPress={handleSubmit}
          style={styles.actionButton}
          textStyle={styles.submitButtonText}
        />
      </View>
    </View>
  );
}
type styleTypes = {
  container: ViewStyle;
  formSecondSection: ViewStyle;
  heading: TextStyle;
  form: ViewStyle;
  icon: ImageStyle;
  actionButton: ViewStyle;
  actionButtonWrapper: ViewStyle;
  formGrid: ViewStyle;
  submitButtonText: TextStyle;
  dropDownStyle: ViewStyle;
  dateOfJoinCalender: ViewStyle;
  dateOfBirthCalender: ViewStyle;
};
const styles: styleTypes = StyleSheet.create({
  dateOfBirthCalender: {
    right: 0,
    bottom: '38%',
  },
  dateOfJoinCalender: {
    left: '11%',
    bottom: '12%',
  },
  dropDownStyle: {
    bottom: '12%',
    width: 300,
    right: 0,
  },
  formSecondSection: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr',
    columnGap: spacing['6'],
    rowGap: spacing['4'],
  },
  container: {
    alignItems: 'center',
    paddingHorizontal: spacing['10'],
    paddingVertical: spacing['5'],
  },
  formFirstSection: {gap: spacing['6']},
  submitButtonText: {color: colors.palette.white},
  actionButtonWrapper: {
    width: '100%',
    display: 'grid',
    zIndex: -10,
    gridTemplateColumns: '1fr 1fr',
    justifyContent: 'center',
    gap: spacing['6'],
  },
  icon: {
    resizeMode: 'contain',
    height: 14,
    width: 14,
  },
  formGrid: {
    columnGap: spacing['6'],
    display: 'grid',
    rowGap: spacing['4'],
    gridTemplateRows: 'repeat(5,1fr)',
    gridTemplateColumns: '1fr 1fr',
  },
  actionButton: {
    width: '100%',
    minWidth: 0,
  },
  heading: {
    fontWeight: 600,
    fontSize: 40,
    color: colors.palette.brandBlue,
  },
  form: {
    width: '100%',
    maxWidth: 700,
    padding: spacing['4'],
    gap: spacing['4'],
  },
});
